import { getDb } from '@dia/db';
import { forecastUrl, parseForecast, type Forecast } from './weatherCache';

/**
 * Weather in Dexie. One row in the meta table, overwritten on every good
 * fetch. Offline, the screen reads it back and says how old it is.
 */

const KEY = 'guest.weather.forecast';

export type CachedForecast = { fetchedAt: number; forecast: Forecast };

export async function readCachedForecast(): Promise<CachedForecast | null> {
  try {
    const row = await getDb().meta.get(KEY);
    const value = row?.value as CachedForecast | undefined;
    return value && typeof value.fetchedAt === 'number' && value.forecast ? value : null;
  } catch {
    return null;
  }
}

async function writeCachedForecast(value: CachedForecast): Promise<void> {
  try {
    await getDb().meta.put({ key: KEY, value });
  } catch {
    // Storage full or blocked. The screen still has the live reading.
  }
}

/** Fetches, caches, and returns. Null means there was no signal or a bad reply. */
export async function fetchForecast(signal?: AbortSignal): Promise<CachedForecast | null> {
  try {
    const response = await fetch(forecastUrl(), { signal });
    if (!response.ok) return null;
    const forecast = parseForecast(await response.json());
    if (!forecast) return null;
    const value = { fetchedAt: Date.now(), forecast };
    await writeCachedForecast(value);
    return value;
  } catch {
    return null;
  }
}
